import { useCallback, useEffect, useRef, useState } from 'react';

import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import { useAppConfigContext } from '../../providers/AppConfigProvider/AppConfigProvider';
import { createLogger, LOG_TOPIC } from '../../utils/devMode/devMode';
import {
  clearShoppingAgentSession,
  getShoppingAgentToken,
  getShoppingAgentTokenCache,
  updateGoogleShoppingAgentSession
} from '../../utils/shoppingAgentSession/shoppingAgentSession';
import { useListenForGlobal } from '../useListenForGlobal/useListenForGlobal';

// Consumers import this from the repeated subpath
// `@ulta/core/hooks/useGoogleShoppingAgent/useGoogleShoppingAgent` instead of a
// local `index.js` barrel. Keep both the named export and the default export so
// named imports and namespace-style test imports stay stable.
export const GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS = {
  ClearAndRefresh: 'clearAndRefreshShoppingAgentToken',
  Refresh: 'refreshShoppingAgentToken'
};

/**
 * FSM phase constants — no magic strings.
 * Idle → Clearing/Refreshing → Cooldown → Idle
 */
export const SHOPPING_AGENT_PHASE = {
  Idle: 'idle',
  Clearing: 'clearing',
  Refreshing: 'refreshing',
  Cooldown: 'cooldown'
};

/**
 * Cooldown duration (ms) after a token operation completes.
 * Prevents retry storms and duplicate work during rapid auth transitions.
 * Follow-up: wire through SSR → appConfig pipeline for env-level override.
 */
export const SHOPPING_AGENT_COOLDOWN_MS = 2500;

const DEFAULT_SHOPPING_AGENT_STATE = {
  phase: SHOPPING_AGENT_PHASE.Idle,
  lastTokenStatus: 'idle',
  lastTokenError: null
};

export const getShoppingAgentDependencyReady = () => {
  const hasGoogleAgent = !!global?.googleAgent;
  return hasGoogleAgent && global.shoppingAgentLoaded && !!global.ULTA_SDK?.shoppingAgentModel?.tokenaction?.get;
};

export const getInitialShoppingAgentState = () => ( {
  ...DEFAULT_SHOPPING_AGENT_STATE
} );

export const resolveShoppingAgentSideEffect = ( sideEffects = [] ) => {
  if( !Array.isArray( sideEffects ) || sideEffects.length === 0 ){
    return null;
  }

  if( sideEffects.find( sideEffect => sideEffect?.[GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS.ClearAndRefresh] ) ){
    return GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS.ClearAndRefresh;
  }

  if( sideEffects.find( sideEffect => sideEffect?.[GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS.Refresh] ) ){
    return GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS.Refresh;
  }

  return null;
};

export const buildShoppingAgentTokenStatus = ( shoppingAgentState ) => ( {
  status: shoppingAgentState?.lastTokenStatus || 'idle',
  error: shoppingAgentState?.lastTokenError || null
} );

export const buildShoppingAgentLogValue = ( data = {} ) => {
  const {
    shoppingAgentState = {},
    sideEffectType = null,
    shoppingAgentTokenStatus = buildShoppingAgentTokenStatus( shoppingAgentState )
  } = handleEmptyObjects( data );

  return {
    source: 'googleShoppingAgent',
    sideEffectType,
    phase: shoppingAgentState.phase || SHOPPING_AGENT_PHASE.Idle,
    lastTokenStatus: shoppingAgentState.lastTokenStatus || 'idle',
    lastTokenError: shoppingAgentState.lastTokenError || null,
    status: shoppingAgentTokenStatus.status,
    error: shoppingAgentTokenStatus.error
  };
};

let _shoppingAgentLogger;
const getShoppingAgentLogger = () => {
  if( !_shoppingAgentLogger ){
    _shoppingAgentLogger = createLogger( {
      topic: LOG_TOPIC.Session,
      scope: '[SDK] ShoppingAgent >'
    } );
  }
  return _shoppingAgentLogger;
};

export const logShoppingAgentEvent = ( message, data = {} ) => getShoppingAgentLogger().log( message, buildShoppingAgentLogValue( data ) );

export const syncShoppingAgentTokenStatus = ( data = {} ) => {
  const { shoppingAgentState = {} } = handleEmptyObjects( data );
  const shoppingAgentTokenStatus = buildShoppingAgentTokenStatus( shoppingAgentState );

  global.ULTA_SDK = {
    ...( global.ULTA_SDK || {} ),
    shoppingAgentToken: shoppingAgentTokenStatus
  };

  logShoppingAgentEvent( 'token status synced', {
    shoppingAgentState,
    shoppingAgentTokenStatus
  } );

  return shoppingAgentTokenStatus;
};

/**
 * When the SDK dependency becomes ready, check if we were blocked on it
 * and transition from Idle to processing if there's a queued side effect type.
 */
export const handleShoppingAgentDependencyReady = ( data = {}, methods = {} ) => {
  const { pendingSideEffectRef = {} } = handleEmptyObjects( data );
  const { setShoppingAgentState } = handleEmptyObjects( methods );

  if( !setShoppingAgentState ){
    return;
  }

  const queuedSideEffect = pendingSideEffectRef.current;
  pendingSideEffectRef.current = null;

  logShoppingAgentEvent( 'dependency ready', {
    sideEffectType: queuedSideEffect || null
  } );

  if( !queuedSideEffect ){
    return;
  }

  const targetPhase = getPhaseForSideEffect( queuedSideEffect );

  setShoppingAgentState( currentState => {
    const nextState = {
      ...currentState,
      phase: targetPhase
    };

    logShoppingAgentEvent( `phase transition: ${currentState.phase} → ${nextState.phase} (replaying queued)`, {
      shoppingAgentState: nextState,
      sideEffectType: queuedSideEffect
    } );

    return nextState;
  } );
};

/**
 * Determines the target phase for a given side effect type.
 *
 * @param {string} sideEffectType - resolved side effect constant
 * @returns {string} SHOPPING_AGENT_PHASE.Clearing or SHOPPING_AGENT_PHASE.Refreshing
 */
export const getPhaseForSideEffect = ( sideEffectType ) => {
  if( sideEffectType === GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS.ClearAndRefresh ){
    return SHOPPING_AGENT_PHASE.Clearing;
  }

  return SHOPPING_AGENT_PHASE.Refreshing;
};

/**
 * Executes the token request for the current phase.
 * Manages abort controller, cooldown timer, and generation tracking.
 */
export const executeTokenRequest = async( data = {}, methods = {} ) => {
  const {
    isMountedRef = {},
    shoppingAgentStateRef = {},
    abortControllerRef = {},
    generationRef = {},
    cooldownTimerRef = {},
    cooldownMs = SHOPPING_AGENT_COOLDOWN_MS
  } = handleEmptyObjects( data );
  const { setShoppingAgentState } = handleEmptyObjects( methods );

  const shoppingAgentState = shoppingAgentStateRef.current;
  const currentPhase = shoppingAgentState?.phase;

  if(
    !setShoppingAgentState ||
    ( currentPhase !== SHOPPING_AGENT_PHASE.Clearing && currentPhase !== SHOPPING_AGENT_PHASE.Refreshing )
  ){
    return;
  }

  const isClear = currentPhase === SHOPPING_AGENT_PHASE.Clearing;
  const existingTokenCache = !isClear ? getShoppingAgentTokenCache() : null;

  // Track generation so we can discard stale responses after abort
  const generation = ( generationRef.current || 0 ) + 1;
  generationRef.current = generation;

  // Create a new AbortController for this request
  const controller = new AbortController();
  abortControllerRef.current = controller;

  setShoppingAgentState( currentState => {
    const nextState = {
      ...currentState,
      lastTokenStatus: 'pending',
      lastTokenError: null
    };

    logShoppingAgentEvent( 'token request started', {
      shoppingAgentState: nextState,
      sideEffectType: isClear ? GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS.ClearAndRefresh : GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS.Refresh
    } );

    return nextState;
  } );

  if( isClear ){
    clearShoppingAgentSession();
  }

  let tokenCache = null;

  try {
    tokenCache = await getShoppingAgentToken( {
      forceRefresh: true,
      signal: controller.signal,
      ...( !isClear && { sessionId: existingTokenCache?.sessionId || null } )
    } );
  }
  catch ( error ){
    tokenCache = null;
  }

  // Imperatively push token to Google agent (belt-and-suspenders)
  if( tokenCache ){
    updateGoogleShoppingAgentSession( tokenCache );
  }

  // Discard response if unmounted or generation is stale (aborted)
  if( !isMountedRef.current || generationRef.current !== generation ){
    return;
  }

  // Transition to cooldown
  setShoppingAgentState( currentState => {
    const nextState = {
      ...currentState,
      phase: SHOPPING_AGENT_PHASE.Cooldown,
      lastTokenStatus: tokenCache ? 'success' : 'error',
      lastTokenError: tokenCache ? null : 'Unable to request token cache'
    };

    logShoppingAgentEvent(
      tokenCache ? 'token request succeeded' : 'token request failed',
      { shoppingAgentState: nextState }
    );

    return nextState;
  } );

  logShoppingAgentEvent( 'entering cooldown', {
    shoppingAgentState: shoppingAgentStateRef.current
  } );

  // Start cooldown timer
  clearTimeout( cooldownTimerRef.current );
  cooldownTimerRef.current = setTimeout( () => {
    if( !isMountedRef.current ){
      return;
    }

    setShoppingAgentState( currentState => {
      if( currentState.phase !== SHOPPING_AGENT_PHASE.Cooldown ){
        return currentState;
      }

      const nextState = {
        ...currentState,
        phase: SHOPPING_AGENT_PHASE.Idle
      };

      logShoppingAgentEvent( 'cooldown expired', {
        shoppingAgentState: nextState
      } );

      return nextState;
    } );
  }, cooldownMs );
};

/**
 * FSM gate: determines whether to accept, ignore, or preempt based on
 * current phase and incoming side effect type.
 *
 * Transition rules:
 * - Idle → accept (start Clearing or Refreshing)
 * - Clearing + anything → ignore (clear always wins)
 * - Refreshing + ClearAndRefresh → abort refresh, start Clearing
 * - Refreshing + Refresh → ignore (already refreshing)
 * - Cooldown + anything → ignore
 */
export const composeProcessShoppingAgentSideEffect = ( data = {}, methods = {} ) => ( args ) => {
  const { shoppingAgentStateRef = {}, abortControllerRef = {}, pendingSideEffectRef = {} } = handleEmptyObjects( data );
  const { setShoppingAgentState } = handleEmptyObjects( methods );
  const { sideEffects = [] } = handleEmptyObjects( args );
  const sideEffectType = resolveShoppingAgentSideEffect( sideEffects );

  logShoppingAgentEvent( 'side effect received', {
    shoppingAgentState: shoppingAgentStateRef.current,
    sideEffectType
  } );

  if( !sideEffectType || !setShoppingAgentState ){
    logShoppingAgentEvent( 'side effect ignored (no type)', {
      shoppingAgentState: shoppingAgentStateRef.current,
      sideEffectType
    } );
    return;
  }

  const currentPhase = shoppingAgentStateRef.current?.phase || SHOPPING_AGENT_PHASE.Idle;
  const isDependencyReady = getShoppingAgentDependencyReady();

  // Cooldown: ignore everything
  if( currentPhase === SHOPPING_AGENT_PHASE.Cooldown ){
    logShoppingAgentEvent( `side effect ignored (phase: ${SHOPPING_AGENT_PHASE.Cooldown})`, {
      shoppingAgentState: shoppingAgentStateRef.current,
      sideEffectType
    } );
    return;
  }

  // Clearing: ignore everything (clear always wins)
  if( currentPhase === SHOPPING_AGENT_PHASE.Clearing ){
    logShoppingAgentEvent( `side effect ignored (phase: ${SHOPPING_AGENT_PHASE.Clearing})`, {
      shoppingAgentState: shoppingAgentStateRef.current,
      sideEffectType
    } );
    return;
  }

  // Refreshing + Refresh: ignore (already refreshing)
  if( currentPhase === SHOPPING_AGENT_PHASE.Refreshing &&
      sideEffectType === GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS.Refresh ){
    logShoppingAgentEvent( `side effect ignored (phase: ${SHOPPING_AGENT_PHASE.Refreshing})`, {
      shoppingAgentState: shoppingAgentStateRef.current,
      sideEffectType
    } );
    return;
  }

  // Refreshing + ClearAndRefresh: abort refresh, start clearing
  if( currentPhase === SHOPPING_AGENT_PHASE.Refreshing &&
      sideEffectType === GOOGLE_SHOPPING_AGENT_SIDE_EFFECTS.ClearAndRefresh ){
    logShoppingAgentEvent( 'aborting refresh for clear', {
      shoppingAgentState: shoppingAgentStateRef.current,
      sideEffectType
    } );
    abortControllerRef.current?.abort();
  }

  // Idle or preempted Refreshing → accept and transition
  const targetPhase = getPhaseForSideEffect( sideEffectType );

  if( !isDependencyReady ){
    pendingSideEffectRef.current = sideEffectType;
    logShoppingAgentEvent( 'side effect queued (dependency not ready)', {
      shoppingAgentState: shoppingAgentStateRef.current,
      sideEffectType
    } );
    return;
  }

  setShoppingAgentState( currentState => {
    const nextState = {
      ...currentState,
      phase: targetPhase
    };

    logShoppingAgentEvent( `phase transition: ${currentPhase} → ${nextState.phase}`, {
      shoppingAgentState: nextState,
      sideEffectType
    } );

    return nextState;
  } );
};

export const useGoogleShoppingAgent = ( data, methods ) => {
  const {} = data || {};
  const {} = methods || {};
  const { shoppingAgentCooldownMs } = useAppConfigContext();
  const cooldownMs = shoppingAgentCooldownMs || SHOPPING_AGENT_COOLDOWN_MS;
  const [shoppingAgentState, setShoppingAgentState] = useState( () => getInitialShoppingAgentState() );
  const shoppingAgentStateRef = useRef( shoppingAgentState );
  const isMountedRef = useRef( false );
  const abortControllerRef = useRef( null );
  const generationRef = useRef( 0 );
  const cooldownTimerRef = useRef( null );
  const pendingSideEffectRef = useRef( null );

  useEffect( () => {
    shoppingAgentStateRef.current = shoppingAgentState;
  }, [shoppingAgentState] );

  useEffect( () => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearTimeout( cooldownTimerRef.current );
      abortControllerRef.current?.abort();
    };
  }, [] );

  useEffect( () => {
    syncShoppingAgentTokenStatus( { shoppingAgentState } );
  }, [shoppingAgentState] );

  const onShoppingAgentDependencyReady = useCallback( () => {
    handleShoppingAgentDependencyReady(
      { pendingSideEffectRef },
      { setShoppingAgentState }
    );
  }, [] );

  useListenForGlobal( {
    watch: getShoppingAgentDependencyReady,
    callback: onShoppingAgentDependencyReady,
    delay: 300
  } );

  const processShoppingAgentSideEffect = useCallback(
    composeProcessShoppingAgentSideEffect(
      { shoppingAgentStateRef, abortControllerRef, pendingSideEffectRef },
      { setShoppingAgentState }
    ),
    []
  );

  // Execute token request when phase transitions to Clearing or Refreshing
  useEffect( () => {
    const phase = shoppingAgentState.phase;

    if( phase !== SHOPPING_AGENT_PHASE.Clearing && phase !== SHOPPING_AGENT_PHASE.Refreshing ){
      return;
    }

    executeTokenRequest(
      { isMountedRef, shoppingAgentStateRef, abortControllerRef, generationRef, cooldownTimerRef, cooldownMs },
      { setShoppingAgentState }
    );
  }, [shoppingAgentState.phase] );

  return {
    processShoppingAgentSideEffect
  };
};

export default useGoogleShoppingAgent;
